import { BASE_URL } from "@config/SitemapConstants";
import { GTMData } from "@helper/gtmData";
import { useNewRelic } from "@hooks/useNewRelic";
import { USFOrgSchema } from "@sphtech/web2-seo-schema";
import React, { useEffect } from "react";
import {
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
  useMatches,
} from "react-router";

type OauthLayoutProps = {
  children: React.ReactNode;
};

export function BaseLayout({ children }: { children: React.ReactNode }) {
  const matches = useMatches();
  useNewRelic();

  const gtm = matches.map((match) => {
    const loaderGtm =
      match.loaderData &&
      typeof match.loaderData === "object" &&
      "gtm" in match.loaderData
        ? (match.loaderData.gtm as GTMData)
        : undefined;
    return loaderGtm;
  });

  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <meta content="width" name="MobileOptimized" />
        <meta content="true" name="HandheldFriendly" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta content="ie=edge" httpEquiv="x-ua-compatible" />

        {/* Inject GTM data server-side */}
        <script
          suppressHydrationWarning // Add this to prevent hydration warnings from cookie logic
          dangerouslySetInnerHTML={{
            __html: `
            // Initialize GTM data layer from route handles
            var gtmdataLayer = ${JSON.stringify(gtm.filter(Boolean)[0] || {})};

            // Set defaults
            gtmdataLayer.visitorcat = gtmdataLayer.visitorcat || 1;
            gtmdataLayer.at = gtmdataLayer.at || "";
            gtmdataLayer.svc = gtmdataLayer.svc || "";
            gtmdataLayer.contentcat = gtmdataLayer.contentcat || 1;
            gtmdataLayer.cd_version = gtmdataLayer.cd_version || "tabla2";
            gtmdataLayer.pagination = gtmdataLayer.pagination || 1;

            // Initialize data layer
            window.dataLayer = window.dataLayer || [];

            // Update with cookies if available (will run client-side)
            if (typeof window !== 'undefined' && typeof window.getCookieByName === 'function') {
              var visitorcat = window.getCookieByName("visitorcat");
              var mysphw = window.getCookieByName("mysphw");
              var svc = window.getCookieByName("svc");

              if (visitorcat != undefined) {
                gtmdataLayer.visitorcat = visitorcat;
              }
              if (mysphw != undefined) {
                gtmdataLayer.at = mysphw;
              }
              if (svc != undefined) {
                gtmdataLayer.svc = svc;
              }
            }

            // Set global references
            window.neuron_dataLayer = gtmdataLayer;
            window._data = gtmdataLayer;
            window.dataLayer.push(gtmdataLayer);`,
          }}
        />
        <Meta />
        <Links />
        <USFOrgSchema
          name="Tabla!"
          description="Stay updated with the latest news, events, and inspiring stories from Singapore and beyond. Explore what's happening in your neighborhood."
          url={`${BASE_URL}/`}
          logo={{
            url: "https://www.tabla.com.sg/web2-assets/tabla_logo_dark.jpg",
            width: 384,
            height: 128,
          }}
          sameAs={[
            "https://www.facebook.com/tablasph/",
            "https://www.instagram.com/tabla_sph/",
            "https://www.tiktok.com/@tablatoks",
          ]}
          contactPoint={{
            telephone: "+65 98633894",
            contactType: "Customer Service",
            availableLanguage: "English",
            areaServed: {
              name: "Singapore",
            },
          }}
        />
        <LightboxScript />
      </head>
      <body suppressHydrationWarning>
        {children}
        <div id="mySPHLightbox" suppressHydrationWarning />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function OauthLayout({ children }: OauthLayoutProps) {
  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <Meta />
        <Links />
        <LightboxScript />
      </head>
      <body suppressHydrationWarning>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function LightboxScript(): React.ReactElement {
  // Coerce raw env values to string (or undefined) to avoid assigning `any` to JSX props
  const lightboxSrc = import.meta.env.VITE_MYSPH_LIGHTBOX2_URL
    ? import.meta.env.VITE_MYSPH_LIGHTBOX2_URL
    : undefined;
  const lightboxClientId = import.meta.env.VITE_MYSPH_LIGHTBOX2_CLIENT_ID
    ? import.meta.env.VITE_MYSPH_LIGHTBOX2_CLIENT_ID
    : undefined;

  useEffect(() => {
    if (lightboxSrc) {
      const script = document.createElement("script");
      script.src = lightboxSrc;
      script.type = "module";
      script.async = true;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [lightboxSrc]);
  return (
    <>
      <script
        suppressHydrationWarning
        dangerouslySetInnerHTML={{
          __html: `
              window.mySPHConfig = {
                clientId: "${lightboxClientId}",
                pubId: "tabla",
                language: "en",
                providers: ["google", "googleOneTap", "apple"],
                ssoMode: "manual",
              };

              if (!window.mySPHConfig.clientId || window.mySPHConfig.clientId === "undefined") {
                console.error("VITE_MYSPH_LIGHTBOX2_CLIENT_ID is not defined. Please check your environment variables.");
              }
            `,
        }}
      />
    </>
  );
}
